// Defensive shim — runs as a side-effect import from bootShell.js so it
// fires BEFORE any other module in any surface (canvas, workspace,
// preview, marketplace) gets a chance to construct a Supabase client.
//
// Problem: @supabase/realtime-js v2 ships a `WebSocketFactory.detectEnvironment`
// that reads `globalThis.process.versions.node` to decide whether it's
// running in Node. Parcel bundles MULTIPLE realtime-js copies (one per
// transitive consumer), and at least one copy's detection mis-fires —
// the detector resolves the runtime as Node and throws "Node.js 20
// detected without native WebSocket support" the first time anything
// constructs a SupabaseClient. The crash cascades through every
// `_requestExternal` call — workspaceProject.people, announcements,
// stories, user_documents, user_preferences — so the governance JWT
// never lands, `root.jwtClaims` stays empty, and `AppWorkspaceSwitcher`
// (which reads `root.jwtClaims.orgs`) has nothing to render.
//
// Three-prong defense:
//   1. Pin a real browser `WebSocket` on `globalThis` so the detector's
//      EARLIEST `typeof WebSocket !== 'undefined'` check catches and
//      returns native before any of the runtime-sniffing branches fire.
//   2. AFFIRMATIVELY define `globalThis.process = { versions: {} }` when
//      undefined — every bundled realtime-js copy's `let i =
//      globalThis.process; if (i) { let e = i.versions; if (e && e.node) }`
//      now sees an empty `versions` object and skips the Node branch.
//      Cheaper + more reliable than chasing every createClient site to
//      inject `transport`.
//   3. If something previously polyfilled `process.versions.node`,
//      scrub it. Belt-and-suspenders.
//
// Idempotent — multiple imports / hot reloads / repeat boots are no-ops.
if (typeof window !== 'undefined') {
  // 1. Pin WebSocket.
  if (typeof window.WebSocket !== 'undefined' && !globalThis.WebSocket) {
    globalThis.WebSocket = window.WebSocket
  }

  // 2 + 3. Stub or scrub globalThis.process.
  try {
    if (typeof globalThis.process === 'undefined') {
      Object.defineProperty(globalThis, 'process', {
        value: { versions: {} },
        writable: true,
        configurable: true,
        enumerable: false
      })
    } else if (globalThis.process.versions && globalThis.process.versions.node) {
      try { globalThis.process.versions.node = undefined } catch (_) {
        try { delete globalThis.process.versions.node } catch (__) {}
      }
    }
  } catch (_) { /* read-only — nothing more we can do */ }
}
